import React from 'react';
import { TypeAddOnProduct } from '@/lib/api/productCatalog/types';
import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';
import { GenInternalLink } from '@/components/generics/GenInternalLink';
import { getPlayKitProductDescription } from '@/utils/productHelpers';
import Image from 'next/image';
import styles from '../../../PlayKitCard/components/AddOnDetails/AddOnDetails.module.scss';
import classNames from 'classnames';
import { trackClickEvent } from '../../utils';
import { CTA_NAMES } from '@/utils/rudderstack';

type BookBundleUpsellProps = {
  title: string;
  linkHref: string;
  linkCopy: string;
  bookBundleUpsellProducts: TypeAddOnProduct[];
};

const BookBundleUpsell: React.FC<BookBundleUpsellProps> = ({
  title,
  linkHref,
  linkCopy,
  bookBundleUpsellProducts,
}) => {
  const displayBundleName = bookBundleUpsellProducts.length > 1;
  const isSingleBundle = bookBundleUpsellProducts.length === 1;

  return (
    <div className="pt-5">
      <div className="flex flex-col md:flex-row md:gap-4 gap-0.5 pb-4 md:items-end items-start">
        <Heading
          classes="w-max"
          tag={HeadingTag.H3}
          size={HeadingSize.H3}
          text={title}
        />
        {linkCopy && linkHref && (
          <GenInternalLink
            classes="text-base md:text-xl"
            href={linkHref}
            cta={linkCopy}
            onClick={() => {
              trackClickEvent(CTA_NAMES.ADD_BOOKS_ADDON);
            }}
          />
        )}
      </div>

      <div
        className={classNames(
          'flex flex-row flex-nowrap overflow-x-scroll scroll-smooth gap-2 md:max-h-[216px] max-w-full grow',
          { 'w-full': isSingleBundle },
        )}
      >
        {bookBundleUpsellProducts.map((product, index) => (
          <div
            key={index}
            className={classNames(
              `flex flex-col gap-4 p-6 grow-0 shrink-0 ${styles.dashStyle}`,
              { 'w-full': isSingleBundle, 'sm:max-w-[92%]': !isSingleBundle },
            )}
          >
            {displayBundleName && (
              <p className="font-bold text-center text-pretty md:whitespace-nowrap">
                {product.copy_text.title}
              </p>
            )}

            <div className="flex flex-col md:flex-row gap-3">
              {product.components.map(({ copy_text, media }, compIndex) => (
                <div key={compIndex} className="flex gap-2 w-full">
                  <Image
                    className="md:min-w-[104px] md:min-h-[104px] min-w-[88px] min-h-[88px] object-contain"
                    src={media[0].uri}
                    alt={copy_text.title}
                    height={104}
                    width={104}
                  />
                  <div
                    className={classNames('flex flex-col', {
                      'max-w-[224px]': !isSingleBundle,
                    })}
                  >
                    <p className="text-base font-bold text-pretty">
                      {copy_text.title}
                    </p>
                    <p className="text-sm">
                      {getPlayKitProductDescription(copy_text.description_html)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BookBundleUpsell;
